import { useEffect, useState } from "react";
import { CheckCircle2, Download, FileSpreadsheet, LoaderCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";

const formats = [["csv", "CSV"], ["xlsx", "Excel (.xlsx)"]];

export default function DataExchangePage() {
  const [datasets, setDatasets] = useState([]);
  const [entity, setEntity] = useState("products");
  const [format, setFormat] = useState("xlsx");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/data-exchange/config").then((response) => {
      setDatasets(response.data);
      if (response.data.length) setEntity(response.data[0].entity);
    }).catch((error) => toast.error(getError(error)));
  }, []);

  const current = datasets.find((dataset) => dataset.entity === entity);
  const download = async (path, filename) => {
    try {
      const response = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const buildData = () => {
    const data = new FormData();
    data.append("file", file);
    data.append("mapping", JSON.stringify(mapping));
    return data;
  };

  const previewFile = async () => {
    if (!file) return toast.error("Choisissez un fichier CSV ou XLSX");
    setLoading(true);
    try {
      const response = await api.post(`/data-exchange/preview/${entity}`, buildData());
      setPreview(response.data);
      setMapping(response.data.mapping || {});
      if (response.data.errors.length || !response.data.valid_count) {
        toast.error(`${response.data.errors.length || response.data.row_count} ligne(s) doivent être corrigée(s)`);
      } else {
        toast.success(`${response.data.valid_count} ligne(s) prête(s) à importer`);
      }
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  };

  const importFile = async () => {
    if (!file || !preview?.valid_count || preview.errors.length) return;
    setLoading(true);
    try {
      const response = await api.post(`/data-exchange/import/${entity}`, buildData());
      toast.success(`${response.data.created} créé(s), ${response.data.updated} mis à jour`);
      setFile(null);
      setPreview(null);
      setMapping({});
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  };

  return <div className="page-enter" data-testid="data-exchange-page">
    <div className="page-heading"><div><p className="eyebrow">IMPORT & EXPORT</p><h1>Échange de données</h1><p>Prévisualisez, associez les colonnes et importez vos fichiers sans surprise.</p></div></div>
    <section className="data-panel" data-testid="data-exchange-controls-panel"><div className="panel-heading"><div><p className="eyebrow">JEU DE DONNÉES</p><h2>Préparer un échange</h2></div><FileSpreadsheet size={20} /></div><div className="data-exchange-controls">
      <label><span>Données</span><select data-testid="data-exchange-entity-select" onChange={(event) => { setEntity(event.target.value); setPreview(null); setMapping({}); }} value={entity}>{datasets.map((dataset) => <option key={dataset.entity} value={dataset.entity}>{dataset.label}</option>)}</select></label>
      <label><span>Format d’export</span><select data-testid="data-exchange-format-select" onChange={(event) => setFormat(event.target.value)} value={format}>{formats.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <button className="secondary-action" data-testid="download-data-template-button" onClick={() => download(`/data-exchange/template/${entity}`, `modele-${entity}.csv`)} type="button"><Download size={16} /> Modèle CSV</button>
      <button className="primary-action" data-testid="export-data-button" onClick={() => download(`/data-exchange/export/${entity}?format=${format}`, `${entity}.${format}`)} type="button"><Download size={16} /> Exporter</button>
    </div></section>
    <section className="data-panel products-panel" data-testid="data-import-panel"><div className="panel-heading"><div><p className="eyebrow">IMPORT GUIDÉ</p><h2>Contrôler avant d’écrire</h2></div><Upload size={20} /></div><div className="data-import-body">
      <label className="data-import-file"><span>Fichier CSV ou XLSX (5 Mo maximum)</span><input accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-testid="data-import-file-input" onChange={(event) => { setFile(event.target.files?.[0] || null); setPreview(null); setMapping({}); }} type="file" /></label>
      <button className="secondary-action" data-testid="preview-data-import-button" disabled={!file || loading} onClick={previewFile} type="button">{loading ? <LoaderCircle className="data-exchange-spinner" size={16} /> : <FileSpreadsheet size={16} />} Vérifier le fichier</button>
    </div>
      {preview && <div className="data-import-preview" data-testid="data-import-preview"><div className="data-import-status"><CheckCircle2 size={18} /><strong>{preview.valid_count} ligne(s) valide(s) sur {preview.row_count}</strong><span>{preview.errors.length ? `${preview.errors.length} erreur(s) à corriger` : "Aucune erreur détectée"}</span></div><div className="data-mapping-grid">{current?.fields.map((field) => <label key={field.key}><span>{field.label}{field.required ? " *" : ""}</span><select data-testid={`data-import-map-${field.key}`} onChange={(event) => setMapping((old) => ({ ...old, [field.key]: event.target.value }))} value={mapping[field.key] || ""}><option value="">Ne pas associer</option>{preview.headers.map((header) => <option key={header} value={header}>{header}</option>)}</select></label>)}</div>
      {preview.errors.length > 0 && <ul className="data-import-errors" data-testid="data-import-errors">{preview.errors.map((error) => <li key={error.row}>Ligne {error.row} : {error.messages.join(" · ")}</li>)}</ul>}
      <div className="responsive-table"><table data-testid="data-import-sample-table"><thead><tr>{current?.fields.map((field) => <th key={field.key}>{field.label}</th>)}</tr></thead><tbody>{preview.sample_rows.map((row, index) => <tr key={index}>{current?.fields.map((field) => <td key={field.key}>{row[field.key] || "—"}</td>)}</tr>)}</tbody></table></div>
      <button className="primary-action" data-testid="confirm-data-import-button" disabled={loading || !preview.valid_count || preview.errors.length > 0} onClick={importFile} type="button">{loading ? <LoaderCircle className="data-exchange-spinner" size={16} /> : <Upload size={16} />} Importer les données validées</button>
    </div>}
    </section>
  </div>;
}